import api from 'services/api';
import { REJECT, array, num, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// A PROJECTION CONTRACT over `GET /connections` — see ../../README.md. Self-contained on
// purpose: its own type, its own schema, shared with nothing. The route's list read
// still belongs to `redux/slices/connectionsSlice`, which owns the list page, and a
// picker reads three of its fields.
//
// Declaring only those is what lets a picker refuse a degraded answer without
// refusing on drift it does not care about. `object()` ignores the rest of the
// payload (parameters, workers, folder path, the list page's header config), so
// nothing else is parsed and nothing else can warn.
//
// Backend: routes/connections.js via modules/generic_functions/getConnections.js.
// Supported query parameters (all optional but `limit`, whose backend default is 10):
//   search           ILIKE '%search%' on the name alone
//   connection_name  ILIKE on the name too; interchangeable with `search`
//   connection_type  case-insensitive equals ('rest', 'ssh', …)
//   connection_id    exact equals (an array becomes `= ANY`) — resolves a stored id to its label
//   sortColumn       one of an allow-list; 'connection_name' is the one we use
//   page / offset    `offset` takes precedence

/** A connection as a picker deals in it. */
export interface ConnectionSummary {
  readonly connection_id: number;
  readonly connection_name: string;
  readonly connection_type: string;
}

const summarySchema = object<ConnectionSummary>({
  connection_id: num(REJECT),
  connection_name: str(),
  connection_type: str(),
});

interface ConnectionsSearchResponse {
  readonly connections: readonly ConnectionSummary[];
  readonly pagination: { readonly total_records: number };
}

const responseSchema = object<ConnectionsSearchResponse>({
  connections: array(summarySchema),
  // Parsed to a number server-side, so a string here is drift.
  pagination: object<{ readonly total_records: number }>({ total_records: num(REJECT) }),
});

export interface SearchConnectionsParams {
  readonly limit: number;
  readonly search?: string;
  readonly connection_type?: string;
  readonly connection_id?: number;
}

/** One page of options, with the server-side count of all matches. */
export interface ConnectionsSearchPage {
  readonly items: readonly ConnectionSummary[];
  readonly total: number;
}

// In-flight de-duplication, keyed on the exact query: independent callers that ask
// the same question at the same time share one request rather than one each.
// Nothing is cached past settlement — asking again later is a genuinely new
// question, since the underlying records change under us.
const inFlight = new Map<string, Promise<ConnectionsSearchPage>>();

// Key on the query's entries, sorted, so two callers that build the same query with
// their keys in a different order still share one request.
const queryKey = (query: SearchConnectionsParams): string =>
  JSON.stringify(
    Object.entries(query)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : 1))
  );

/**
 * One page of connections matching `params`, ordered by name.
 *
 * Name order is fixed here rather than left to callers, so that no caller can
 * present an unordered list — the route's own default is `created_at`. The column
 * is `COLLATE "C"`, so it is byte order: every uppercase initial sorts before every
 * lowercase one.
 *
 * Collapses with `refuseDegraded`, and that is a property of this contract rather
 * than a policy imposed on an unknown consumer: an option missing from the list is
 * one the user cannot pick, and one they picked before no longer resolves. A
 * partial list of values is not a list of values.
 *
 * `undefined` params are dropped by axios, so omitting one widens the query rather
 * than filtering on empty.
 */
export const searchConnections = async (params: SearchConnectionsParams): Promise<ConnectionsSearchPage> => {
  const key = queryKey(params);
  const pending = inFlight.get(key);
  if (pending !== undefined) return pending;

  const request = api
    .get('/connections', { params: { ...params, sortColumn: 'connection_name', sortOrder: 'ASC' } })
    .then((res) => refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'searchConnections' })))
    .then(({ connections, pagination }) => ({ items: connections, total: pagination.total_records }))
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
};
